import { inject, Injectable } from '@angular/core';
import { ResourceType } from '@thiememeulenhoff/types';
import { UnleashFlags, UnleashService } from '@thiememeulenhoff/ng-sdk';
import { ServicesService } from '../services';

@Injectable({
  providedIn: 'root',
})
export class ContentEndpointsService {
  readonly #service = inject(ServicesService);

  readonly #unleash = inject(UnleashService);

  #contentBackend(endpoint: string, specificFlag?: UnleashFlags) {
    if (specificFlag && this.#unleash.isEnabled(specificFlag)) {
      return this.#service.vesta(endpoint);
    }

    return this.#service.thip(endpoint);
  }

  public arrangements() {
    return this.#contentBackend(
      `arrangement`,
      UnleashFlags.UseVestaArrangements,
    );
  }

  public arrangementByCode(arrangementCode: string) {
    return this.#contentBackend(
      `arrangement/${arrangementCode}`,
      UnleashFlags.UseVestaArrangementByCode,
    );
  }

  public saveArrangement(arrangementCode: string) {
    return this.#contentBackend(
      `arrangement/${arrangementCode}/resource`,
      UnleashFlags.UseVestaSaveArrangement,
    );
  }

  public createArrangement() {
    return this.#contentBackend(
      `arrangement`,
      UnleashFlags.UseVestaCreateArrangement,
    );
  }

  public vdexDefinitionByApiField(arrangementCode: string, apiField: string) {
    return this.#contentBackend(
      `arrangement/${arrangementCode}/vdex/${apiField}`,
      UnleashFlags.UseVestaVdexDefinition,
    );
  }

  public clusters() {
    return this.#contentBackend(`cluster`, UnleashFlags.UseVestaClusters);
  }

  public clusterByMethodSlug(methodSlug: string) {
    return this.#contentBackend(
      `method/${methodSlug}/clusters`,
      UnleashFlags.UseVestaClusterByMethodSlug,
    );
  }

  public resourcesByArrangementCode(arrangementCode: string) {
    return this.#contentBackend(
      `arrangement/${arrangementCode}/resource`,
      UnleashFlags.UseVestaResourcesByArrangementCode,
    );
  }

  public resourcesByMethodSlug(
    methodSlug: string,
    resourceType: ResourceType | null = null,
  ) {
    if (resourceType) {
      return this.#contentBackend(
        `method/${methodSlug}/resource/${resourceType}`,
        UnleashFlags.UseVestaResourcesByMethodSlug,
      );
    }

    return this.#contentBackend(
      `method/${methodSlug}/resource`,
      UnleashFlags.UseVestaResourcesByMethodSlug,
    );
  }

  public multipleResources() {
    return this.#contentBackend(
      `arrangement/resource/multiple`,
      UnleashFlags.UseVestaMultipleResources,
    );
  }

  public methods() {
    return this.#contentBackend(`method`, UnleashFlags.UseVestaMethods);
  }

  public methodBySlug(methodSlug: string) {
    return this.#contentBackend(
      `method/${methodSlug}`,
      UnleashFlags.UseVestaMethodBySlug,
    );
  }

  public contentById(streamCode: string, contentIdentifier: string) {
    return this.#contentBackend(
      `arrangement/${streamCode}/content/html/identifier/${contentIdentifier}`,
      UnleashFlags.UseVestaContentById,
    );
  }

  public rawContentById(streamCode: string, contentIdentifier: string) {
    return this.#contentBackend(
      `arrangement/${streamCode}/content/identifier/${contentIdentifier}`,
      UnleashFlags.UseVestaRawContentById,
    );
  }

  public search() {
    return this.#service.thip(`search/resource`);
  }

  contentByPath(streamCode: string, contentPath: string) {
    return this.#contentBackend(
      `arrangement/${streamCode}/content/html/path/${contentPath}`,
      UnleashFlags.UseVestaContentByPath,
    );
  }
}
